import { currencyFormatter } from "@fla/common";
import {
  Button,
  DollarIcon,
  EditIcon,
  FormError,
  PartnerIncomeIcon,
  RadioSelect,
  RangeSlider,
  RealEstatePaymentDetailsProps,
  RealEstateWhatWillHappenSchema,
  RealEstate_InitialState,
  Select,
  TextArea,
  TextInput,
  ThirdPartyIncomeIcon,
  UserIncomeIcon,
  useNames,
  useOptions,
  useToggle
} from "@fla/components";
import HouseIcon from "@fla/components/src/assets/svgs/house.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { Fragment, useState } from "react";
import { UseFormReset, useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import PaymentDetails from "../PaymentDetails";

interface IProps {
  formId: string;
  store: RealEstate_InitialState;
  onSubmit: (values: RealEstate_InitialState) => void;
}

export default function WhatWillHappen({ formId, store, onSubmit }: IProps) {
  const { toggleOn, onToggleClick } = useToggle();
  const [paymentEditData, setPaymentEditData] = useState<Partial<RealEstatePaymentDetailsProps> & { id: number }>();
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const { getName, getAlternateName, userFirstName, spouseFirstName, getBankDetails } = useNames({ initialDetails });

  const {
    realEstatePaymentMethodOptions,
    realEstatePropertyStatusOptions,
    realEstateSaleProceedsOptions,
    realEstateSaleProceedsOwnerOptions,
    realEstateTransferCostsOptions
  } = useOptions({ initialDetails });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    resetField
  } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(RealEstateWhatWillHappenSchema)
  });

  const { append, remove, update } = useFieldArray({
    control,
    name: "payments"
  });

  // const getBankName = (val) => {};
  const closePaymentDetailsModal = () => {
    onToggleClick();
    setPaymentEditData(undefined);
  };

  const addPaymentDetails = () => {
    setPaymentEditData(undefined);
    onToggleClick();
  };

  const onSubmitPaymentDetails = (
    values: RealEstatePaymentDetailsProps,
    reset: UseFormReset<RealEstatePaymentDetailsProps>
  ) => {
    if (paymentEditData) {
      update(paymentEditData.id, values);
    } else {
      append(values);
    }
    closePaymentDetailsModal();
    reset();
  };

  const paymentDetailsProps = {
    isOpen: toggleOn,
    onClose: closePaymentDetailsModal,
    remove,
    editData: paymentEditData,
    onSubmitPaymentDetails
  };

  const netEquity =
    Number(store.value) -
    (Array.isArray(store?.mortgage) ? store.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);
  const totalPayment = watch("payments")?.reduce((p, c) => p + Number(c.value), 0) || 0;
  const totalUserPayment =
    (watch("payments")
      ?.filter(v => v.payment_to === "user")
      .reduce((p, c) => p + Number(c.value), 0) || 0) +
    ((netEquity - totalPayment) * Number(watch("split_balance.user"))) / 100;

  const totalSpousePayment =
    (watch("payments")
      ?.filter(v => v.payment_to === "spouse")
      .reduce((p, c) => p + Number(c.value), 0) || 0) +
    ((netEquity - totalPayment) * Number(watch("split_balance.spouse"))) / 100;

  const totalThirdPartiesPayment =
    watch("payments")
      ?.filter(v => v.payment_to === "something_else")
      .reduce((p, c) => p + Number(c.value), 0) || 0;

  return (
    <>
      <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-5 px-10">
          <div className="mb-8 pb-8 border-b flex flex-col items-center justify-center">
            <img src={HouseIcon} className="w-10 h-auto my-3" alt="mortgage" />
            <h3 className="text-center">Real Estate</h3>
            <p className="text-sm">What Will Happen?</p>
          </div>

          <RadioSelect
            type="card-brief"
            title="What will happen to this property?"
            name="property_status"
            id="property_status"
            control={control}
            options={realEstatePropertyStatusOptions}
            onChange={v => {
              if (v === "one_keeps") {
                resetField("sale_proceeds", { defaultValue: "" });
                resetField("sale_proceeds_owner", { defaultValue: "" });
                resetField("split_by_percentage", {
                  defaultValue: { user: "", spouse: "" }
                });
                resetField("payments", { defaultValue: [] });
                resetField("split_balance", {
                  defaultValue: { user: "", spouse: "" }
                });
              }
              if (v === "sell") {
                resetField("property_keeper", { defaultValue: "" });

                resetField("payer", { defaultValue: "" });
                resetField("if_paying_settlement", { defaultValue: "" });
                resetField("payment_method", { defaultValue: "" });
                resetField("payment_by_fixed", { defaultValue: "" });
                resetField("payment_by_percentage", {
                  defaultValue: { user: "", spouse: "" }
                });
                resetField("payment_details", { defaultValue: "" });
                resetField("other_person_amount", { defaultValue: "" });
              }
            }}
          />

          {watch("property_status") === "sell" ? (
            <Fragment>
              <p className="text-base font-semibold mb-2">How will the net sale proceeds be divided?</p>
              <div className="rounded-xl border p-5 space-y-4">
                {store?.if_mortgage_on_property === "yes" ? (
                  <p>
                    Any mortgage on the property needs to be paid out first. After the mortgage is paid out, it leaves
                    the amount of&nbsp;
                    <b>{currencyFormatter.format(netEquity)}</b>.&nbsp; <br />
                    <br />
                    The net sales proceeds are determined by deducting the following costs from the&nbsp;
                    <b>{currencyFormatter.format(netEquity)}</b>:
                  </p>
                ) : (
                  <p>
                    As there is no mortgage on the property, the net sales proceeds are determined by deducting the
                    following costs from the value of the property (which is&nbsp;{" "}
                    <b>{currencyFormatter.format(Number(store?.value))}</b>):
                  </p>
                )}

                <ul className="pl-7 space-y-3">
                  <li className="flex items-center">
                    <div className="w-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                    </div>
                    <p className="w-[calc(100%_-_12px)] pl-3">Any real estate fees and commissions</p>
                  </li>
                  <li className="flex items-center">
                    <div className="w-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                    </div>
                    <p className="w-[calc(100%_-_12px)] pl-3">Settlement agent and Title Registry fees</p>
                  </li>
                  <li className="flex items-center">
                    <div className="w-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                    </div>
                    <p className="w-[calc(100%_-_12px)] pl-3">Other ordinary costs of sale</p>
                  </li>
                </ul>
              </div>

              <RadioSelect
                type="card-brief"
                name="sale_proceeds"
                id="sale_proceeds"
                control={control}
                options={realEstateSaleProceedsOptions}
                classes="my-8"
                onChange={v => {
                  if (v === "split_by_percentage") {
                    setValue("split_by_percentage", {
                      user: "50",
                      spouse: "50"
                    });
                  } else {
                    resetField("split_by_percentage", {
                      defaultValue: { user: "", spouse: "" }
                    });
                  }

                  if (v === "something_else") {
                    setValue("split_balance", { user: "50", spouse: "50" });
                  } else {
                    resetField("split_balance", {
                      defaultValue: { user: "", spouse: "" }
                    });
                    setValue("payments", []);
                  }

                  if (v !== "one_keeps_all") {
                    resetField("sale_proceeds_owner", { defaultValue: "" });
                  }
                }}
              />

              {watch("sale_proceeds") === "one_keeps_all" ? (
                <Select
                  title="Who will keep the proceeds of sale?"
                  control={control}
                  name="sale_proceeds_owner"
                  id="sale_proceeds_owner"
                  placeholder="Select"
                  options={realEstateSaleProceedsOwnerOptions}
                />
              ) : null}
              {watch("sale_proceeds") === "split_by_percentage" ? (
                <RangeSlider
                  title="Split By A Percentage"
                  subTitle="Use the below slider to show the percentage you each will receive:"
                  control={control}
                  name="split_by_percentage.user"
                  id="split_by_percentage.user"
                  exName={initialDetails?.spouse?.first_name || "N/A"}
                  percentageOf={String(netEquity)}
                  onChange={(val: string) => {
                    setValue("split_by_percentage.spouse", `${100 - Number(val)}`);
                  }}
                />
              ) : null}

              {watch("sale_proceeds") === "something_else" ? (
                <Fragment>
                  <div className="rounded-xl border p-5 space-y-4 mb-5">
                    <p>Use this option for the following situations:</p>

                    <ul className="pl-7 space-y-3">
                      <li className="flex items-center">
                        <div className="w-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                        </div>
                        <p className="w-[calc(100%_-_12px)] pl-3">
                          If you are going to be pay out any loans or debts before dividing the remaining net sale
                          proceeds.
                        </p>
                      </li>
                      <li className="flex items-center">
                        <div className="w-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                        </div>
                        <p className="w-[calc(100%_-_12px)] pl-3">
                          If one party is to receive a set amount of the net sale proceeds first, before dividing the
                          remaining net sale proceeds.
                        </p>
                      </li>
                      <li className="flex items-center">
                        <div className="w-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                        </div>
                        <p className="w-[calc(100%_-_12px)] pl-3">
                          If you want to make an allowance to one person for any capital gains tax that may be incurred
                          before dividing the remaining net sale proceeds.
                        </p>
                      </li>
                      <li className="flex items-center">
                        <div className="w-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                        </div>
                        <p className="w-[calc(100%_-_12px)] pl-3">
                          If you want to hold a portion of the net sale proceeds in a joint account for children’s
                          expenses before dividing the remaining net sale proceeds.
                        </p>
                      </li>
                    </ul>
                  </div>

                  <article className="mb-8">
                    <div className="w-full py-10 dashed-border rounded-[10px] flex flex-col items-center justify-center space-y-10">
                      <p className="text-sm font-bold">Add all the payments you want to first</p>
                      <Button
                        className="px-5 py-2 uppercase"
                        fontSize="sm"
                        onClick={addPaymentDetails}
                        type="button"
                        // appearance="outline"
                      >
                        + Add Payment Details
                      </Button>
                    </div>

                    {errors && errors?.payments ? (
                      <section className="flex justify-center my-1">
                        <FormError error={errors?.payments?.message} />
                      </section>
                    ) : null}
                  </article>

                  <hr className="mb-5" />
                  <section className="mb-5 space-y-2">
                    <p>
                      The proceeds of sale of the&nbsp;
                      <span className="font-medium">{`${store.street}, ${store.suburb}, ${store.state}, ${store.postcode}`}</span>
                      &nbsp;will be disbursed in the following order:
                    </p>
                    <ul className="list-disc pl-7">
                      <li className="italic">
                        Discharge of all mortgages on the property&nbsp;
                        <span>
                          {Array.isArray(store?.mortgage) && store?.mortgage?.length
                            ? store.mortgage.map((m, idx) => (
                                <span key={+idx}>
                                  <span className="font-medium">{getBankDetails(m.lender!).label}</span>(
                                  {currencyFormatter.format(Number(m.value))})
                                  {idx === store.mortgage!.length - 1 ? null : `, `}
                                </span>
                              ))
                            : "N/A"}
                        </span>
                      </li>
                      <li>Paying reasonable costs of sale, adjustments, agent fees</li>
                    </ul>

                    <p className="mt-3">and then the following:</p>
                  </section>

                  {Array.isArray(watch("payments")) && watch("payments")!.length
                    ? watch("payments")!.map((p, idx) => (
                        <div key={+idx} className="rounded-xl bg-[#f2f2f2] p-5 mb-5 flex items-center">
                          <div className="w-20">
                            <div className="w-16 h-16 rounded-full bg-[#DEDEDE] flex justify-center items-center">
                              <DollarIcon classes="w-full h-8" />
                            </div>
                          </div>
                          <div className="w-[calc(100%_-_176px)] space-y-2">
                            <p className="text-lg font-semibold">
                              Payment to&nbsp;
                              {p.other_person_entity || getName(p.payment_to)}
                            </p>
                            <div className="flex justify-between items-center space-x-5">
                              <p className="text-sm font-light">{p.description}</p>
                            </div>
                            <button
                              className="text-sm text-primary-faded font-semibold uppercase flex items-center space-x-1"
                              onClick={() => {
                                setPaymentEditData({
                                  ...p,
                                  id: idx
                                });
                                onToggleClick();
                              }}
                              type="button"
                            >
                              <EditIcon classes="w-3.5 h-3.5" />
                              <p className="tracking-wide">Edit</p>
                            </button>
                          </div>
                          <div className="w-24 pl-3 flex justify-end">
                            <p className="text-xl font-bold">{currencyFormatter.format(Number(p.value))}</p>
                          </div>
                        </div>
                      ))
                    : null}
                  <div className="w-full px-8 pt-5 dashed-border rounded-[10px] flex flex-col items-center justify-center space-y-4 mb-8">
                    <RangeSlider
                      title={`How do you wish to split the remaining balance of approx ${currencyFormatter.format(
                        netEquity - totalPayment
                      )} less sales costs`}
                      control={control}
                      name="split_balance.user"
                      id="split_balance.user"
                      exName={initialDetails?.spouse?.first_name || "N/A"}
                      percentageOf={String(netEquity - totalPayment)}
                      onChange={val => {
                        setValue("split_balance.spouse", `${100 - Number(val)}`);
                      }}
                      classes="md:pb-[0px]"
                      boxWidth="5/12"
                    />
                  </div>
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}
        </div>

        {watch("sale_proceeds") === "something_else" ? (
          <section className="bg-[#f2f2f2] px-10">
            <p className="font-bold py-5 text-center">Total Division of proceeds of sale</p>

            <div className="flex justify-between items-center py-10 border-t border-b border-gray-300">
              <div className="w-12">
                <UserIncomeIcon classes="w-10 h-10" />
              </div>
              <p className="w-[calc(100%_-_48px)] pl-3">
                <span className="font-semibold">{userFirstName}</span> receives approx.
              </p>
              <span className="text-xl font-bold">{currencyFormatter.format(totalUserPayment)}</span>
            </div>
            <div
              className={classNames("flex justify-between items-center py-10", {
                "border-b border-gray-300": !!Number(totalThirdPartiesPayment)
              })}
            >
              <div className="w-12">
                <PartnerIncomeIcon classes="w-10 h-10" />
              </div>
              <p className="w-[calc(100%_-_48px)] pl-3">
                <span className="font-semibold">{spouseFirstName}</span>&nbsp; receives approx.
              </p>
              <span className="text-xl font-bold">{currencyFormatter.format(totalSpousePayment)}</span>
            </div>
            {Number(totalThirdPartiesPayment) ? (
              <div className="flex justify-between items-center py-5">
                <div className="w-12">
                  <ThirdPartyIncomeIcon classes="w-10 h-10" />
                </div>
                <p className="w-[calc(100%_-_48px)] pl-3">
                  Payment to&nbsp;
                  <span className="font-semibold">third parties</span>
                </p>
                <span className="text-xl font-bold">{currencyFormatter.format(totalThirdPartiesPayment)}</span>
              </div>
            ) : null}
          </section>
        ) : null}

        <div className="space-y-5 px-10">
          {watch("property_status") === "one_keeps" ? (
            <Fragment>
              <Select
                title="Who will keep the property?"
                control={control}
                name="property_keeper"
                id="property_keeper"
                placeholder="Select"
                subTitle={
                  store?.if_mortgage_on_property === "yes"
                    ? "The person keeping the property will automatically be responsible for the mortgage (eg the mortgage will be refinanced into their name). You can make adjustments to the settlement sum if you want to reduce the amount of the mortgage."
                    : ""
                }
                options={realEstateSaleProceedsOwnerOptions}
              />

              <div className="rounded-xl border p-5 space-y-4 mb-5">
                <p>Transfer costs include:</p>

                <ul className="pl-7 space-y-3">
                  {store?.if_mortgage_on_property === "yes" ? (
                    <li className="flex items-center">
                      <div className="w-1/12">
                        <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                      </div>
                      <p className="w-11/12">
                        Fees for discharging the existing mortgage and registering a new mortgage.
                      </p>
                    </li>
                  ) : null}

                  <li className="flex items-center">
                    <div className="w-1/12">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                    </div>
                    <p className="w-11/12">Settlement agent and Title Registry fees.</p>
                  </li>
                  <li className="flex items-center">
                    <div className="w-1/12">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-faded" />
                    </div>
                    <p className="w-11/12">Other ordinary costs transferring title.</p>
                  </li>
                </ul>
              </div>
              <Select
                title="Who will be paying the transfer costs?"
                control={control}
                name="payer"
                id="payer"
                placeholder="Select"
                options={realEstateTransferCostsOptions}
              />
              <RadioSelect
                title={`Will ${getName(watch("property_keeper"))} pay ${getAlternateName(
                  watch("property_keeper")
                )} a settlement sum for keeping the property?`}
                name="if_paying_settlement"
                id="if_paying_settlement"
                subTitle="Eg a cash payment"
                control={control}
                options={["yes", "no"]}
                onChange={val => {
                  if (val === "no") {
                    resetField("payment_method", { defaultValue: "" });
                    resetField("payment_by_fixed", { defaultValue: "" });
                    resetField("payment_by_percentage", {
                      defaultValue: { user: "", spouse: "" }
                    });
                    resetField("payment_details", { defaultValue: "" });
                    resetField("other_person_amount", { defaultValue: "" });
                  }
                }}
              />
              {watch("if_paying_settlement") === "yes" ? (
                <Fragment>
                  <RadioSelect
                    type="card-brief"
                    title="How will you determine this payment?"
                    name="payment_method"
                    id="payment_method"
                    control={control}
                    options={realEstatePaymentMethodOptions}
                    onChange={v => {
                      if (v === "percentage") {
                        setValue("payment_by_percentage", {
                          user: "50",
                          spouse: "50"
                        });
                        resetField("payment_by_fixed", { defaultValue: "" });
                        resetField("payment_details", { defaultValue: "" });
                        resetField("other_person_amount", { defaultValue: "" });
                      }

                      if (v === "fixed_amount") {
                        setValue("payment_by_percentage", {
                          user: "",
                          spouse: ""
                        });
                        resetField("payment_details", { defaultValue: "" });
                        resetField("other_person_amount", { defaultValue: "" });
                      }

                      if (v === "something_else") {
                        setValue("payment_by_percentage", {
                          user: "",
                          spouse: ""
                        });
                        resetField("payment_by_fixed", { defaultValue: "" });
                      }
                    }}
                  />
                  {watch("payment_method") === "fixed_amount" ? (
                    <TextInput
                      title={`What is the amount payable to ${getAlternateName(watch("property_keeper"))}`}
                      control={control}
                      name="payment_by_fixed"
                      id="payment_by_fixed"
                      placeholder="eg $500,000"
                      isAmount
                    />
                  ) : null}
                  {watch("payment_method") === "percentage" ? (
                    <RangeSlider
                      title="Split By A Percentage"
                      subTitle="Use the below slider to show the percentage you each will receive:"
                      control={control}
                      name="payment_by_percentage.user"
                      id="payment_by_percentage.user"
                      exName={initialDetails?.spouse?.first_name || "N/A"}
                      percentageOf={String(netEquity)}
                      onChange={val => {
                        setValue("payment_by_percentage.spouse", `${100 - Number(val)}`);
                      }}
                    />
                  ) : null}
                  {watch("payment_method") === "something_else" ? (
                    <TextArea
                      title="Please give further details"
                      control={control}
                      name="payment_details"
                      id="payment_details"
                      placeholder="Description"
                    />
                  ) : null}

                  {watch("payment_method") === "something_else" ? (
                    <TextInput
                      title={`How much will ${getAlternateName(watch("property_keeper"))} receive approximately?`}
                      control={control}
                      name="other_person_amount"
                      id="other_person_amount"
                      isAmount
                    />
                  ) : null}
                </Fragment>
              ) : null}
            </Fragment>
          ) : null}
        </div>
      </form>
      <PaymentDetails {...paymentDetailsProps} />
    </>
  );
}
